import { DialogTitle } from '@headlessui/react'
import SimpleModal from '../../../components/SimpleModal'
import { blastRadiusFor, getClassifierById } from '../mockData'
import BlastRadiusTree from './BlastRadiusTree'

interface Props {
    open: boolean
    onClose: () => void
    classifierId: string
    fromVersion: number
    toVersion: number
    profileCount: number
    teamCount: number
    orgCount: number
    // 'rollback' reuses this flow to make an older version live again — same blast
    // radius and typed count, different framing. Defaults to the go-live path so
    // existing call sites are unchanged.
    mode?: 'golive' | 'rollback'
}

export default function GoLiveModal(props: Props) {
    const {
        open,
        onClose,
        classifierId,
        fromVersion,
        toVersion,
        profileCount,
        teamCount,
        orgCount,
        mode = 'golive'
    } = props
    const classifier = getClassifierById(classifierId)
    const title = classifier?.title ?? 'Classifier'
    const isRollback = mode === 'rollback'

    // Surface threshold changes alongside the prompt diff — prompt and thresholds are
    // versioned together, so reviewers should see both before committing. Two queues
    // move together (Borderline, Confirmed); there is no Safe threshold.
    const fromV = classifier?.versions.find((v) => v.version === fromVersion)
    const toV = classifier?.versions.find((v) => v.version === toVersion)
    const borderlineChanged =
        fromV !== undefined &&
        toV !== undefined &&
        fromV.defaultBorderlineThreshold !== toV.defaultBorderlineThreshold
    const confirmedChanged =
        fromV !== undefined &&
        toV !== undefined &&
        fromV.defaultConfirmedThreshold !== toV.defaultConfirmedThreshold

    // The prompt diff is illustrative. On rollback we're reverting, so the lines
    // that were added move back out and the prior lines return — flip the two so
    // the colours and signs read in the direction of the change.
    const addClass = isRollback ? 'bg-severe/10 text-severe' : 'bg-safe/10 text-safe'
    const removeClass = isRollback ? 'bg-safe/10 text-safe' : 'bg-severe/10 text-severe'
    const addSign = isRollback ? '-' : '+'
    const removeSign = isRollback ? '+' : '-'

    const confirmLabel = isRollback
        ? `Roll back to v${toVersion} — ${profileCount} profiles`
        : `Go Live with v${toVersion} — ${profileCount} profiles`

    return (
        <SimpleModal open={open} onClose={onClose}>
            <div className="w-[620px] max-w-[94vw] rounded-xl bg-white shadow-2xl text-sm text-gray-800 text-left overflow-hidden">
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-100">
                    <DialogTitle as="h3" className="font-sans !font-medium text-lg text-gray-900 m-0">
                        {isRollback ? (
                            <>Roll back to v{toVersion} — &ldquo;{title}&rdquo;</>
                        ) : (
                            <>Go live with &ldquo;{title}&rdquo;</>
                        )}{' '}
                        <span className="text-[11px] font-semibold text-gray-500 font-sans">
                            v{fromVersion} → v{toVersion}
                        </span>
                    </DialogTitle>
                    <p className="text-gray-500 text-[12.5px] mt-1 mb-0">
                        {isRollback
                            ? `Rolls every profile currently running this classifier back to v${toVersion}. Profiles stay on v${fromVersion} until you confirm.`
                            : `Updates every profile currently running this classifier to v${toVersion}. Profiles stay on v${fromVersion} until you confirm.`}
                    </p>
                </div>

                {/* Body */}
                <div className="px-5 py-4 max-h-[430px] overflow-y-auto space-y-4">
                    {/* Prompt diff section */}
                    <div>
                        <p className="text-[10.5px] font-bold uppercase tracking-wide text-gray-500 mb-2">
                            What&rsquo;s changing{' '}
                            <span className="normal-case font-normal text-gray-400">— prompt diff</span>
                        </p>
                        <div className="rounded-lg border border-gray-200 overflow-hidden font-mono text-xs">
                            <div className="px-3 py-1 text-gray-500 whitespace-pre-wrap">
                                {'  '}Classify the COMMENT into one of: SAFE, SUSPECT, SEVERE.
                            </div>
                            <div className={`px-3 py-1 ${removeClass} whitespace-pre-wrap`}>
                                {removeSign} targeting a person or protected group (race, religion, gender).
                            </div>
                            <div className={`px-3 py-1 ${addClass} whitespace-pre-wrap`}>
                                {addSign} targeting a person or protected group (race, religion, gender,
                            </div>
                            <div className={`px-3 py-1 ${addClass} whitespace-pre-wrap`}>
                                {addSign} sexuality, disability).
                            </div>
                            <div className="px-3 py-1 text-gray-500 whitespace-pre-wrap">
                                {'  '}Return JSON: {'{ "label": ..., "rationale": ... }'}
                            </div>
                        </div>
                        {(borderlineChanged || confirmedChanged) && (
                            <div className="mt-2 mb-0 space-y-0.5 text-xs text-gray-600">
                                {borderlineChanged && (
                                    <p className="m-0">
                                        Borderline threshold:{' '}
                                        <span className="font-mono tabular-nums">{fromV.defaultBorderlineThreshold.toFixed(2)}</span>
                                        {' → '}
                                        <span className="font-mono tabular-nums font-semibold">{toV.defaultBorderlineThreshold.toFixed(2)}</span>
                                    </p>
                                )}
                                {confirmedChanged && (
                                    <p className="m-0">
                                        Confirmed threshold:{' '}
                                        <span className="font-mono tabular-nums">{fromV.defaultConfirmedThreshold.toFixed(2)}</span>
                                        {' → '}
                                        <span className="font-mono tabular-nums font-semibold">{toV.defaultConfirmedThreshold.toFixed(2)}</span>
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Impact section */}
                    <div>
                        <p className="text-[10.5px] font-bold uppercase tracking-wide text-gray-500 mb-2">
                            Impact
                        </p>

                        {/* Summary box */}
                        <div className="bg-suspect/10 border border-suspect/30 rounded-lg px-3.5 py-3 mb-3">
                            <div className="text-[22px] font-extrabold text-suspect leading-none">
                                <span>{profileCount} profiles</span>
                            </div>
                            <div className="text-suspect text-[12.5px] mt-1">
                                across {teamCount} teams · {orgCount} organisations will move from{' '}
                                <span>v{fromVersion}</span>
                                {' '}to{' '}
                                <span>v{toVersion}</span>
                            </div>
                        </div>

                        <BlastRadiusTree
                            orgs={blastRadiusFor(classifierId)}
                            moreText="+ 5 more organisations · 73 profiles"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="px-5 py-3.5 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                        {isRollback
                            ? `Go live with a newer version to move forward again.`
                            : `This cannot be undone in bulk — you'd go live with v${fromVersion} again to revert.`}
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg bg-arwen-600 px-4 py-2 text-sm font-bold text-white hover:bg-arwen-700"
                        >
                            {confirmLabel}
                        </button>
                    </div>
                </div>
            </div>
        </SimpleModal>
    )
}
